import React from 'react'
// Full-screen media viewer for ad blocks with Miners Market header

export default function AdBlockMediaViewer({ media, blockTitle, onClose }) {
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [touchStart, setTouchStart] = React.useState(null);
    const [touchEnd, setTouchEnd] = React.useState(null);

    if (!media || media.length === 0) return null;

    const currentMedia = media[currentIndex];
    const isPDF = currentMedia.file_type === 'pdf';
    const hasMultiple = media.length > 1;

    const goToPrevious = () => {
        setCurrentIndex(prev => (prev > 0 ? prev - 1 : media.length - 1));
    };

    const goToNext = () => {
        setCurrentIndex(prev => (prev < media.length - 1 ? prev + 1 : 0));
    };

    // Touch handlers for swipe navigation
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const minSwipeDistance = 50;
        if (Math.abs(distance) > minSwipeDistance) {
            if (distance > 0) {
                goToNext();
            } else {
                goToPrevious();
            }
        }
        setTouchStart(null);
        setTouchEnd(null);
    };

    // Keyboard navigation
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') goToPrevious();
            if (e.key === 'ArrowRight') goToNext();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, media.length]);

    return (
        <div className="fixed inset-0 bg-black z-[9999] flex flex-col" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
            {/* Miners Market Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-800">
                <div className="flex items-center gap-3">
                    <img src="images/logo/MM Logo.jpg" alt="Miners Market" className="h-8 w-8 rounded-lg" />
                    <span className="font-bold text-white text-lg">MINERS MARKET</span>
                </div>
                <div className="flex items-center gap-3">
                    {blockTitle && <span className="text-gray-400 text-sm hidden sm:block">{blockTitle}</span>}
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        <i className="fas fa-arrow-left"></i>
                        <span className="text-sm font-medium">Back</span>
                    </button>
                </div>
            </div>

            {/* Media Counter */}
            {hasMultiple && (
                <div className="text-center py-2 bg-gray-900 text-gray-400 text-sm">
                    {currentIndex + 1} / {media.length}
                </div>
            )}

            {/* Media Content */}
            <div className="flex-1 flex items-center justify-center relative overflow-hidden">
                {isPDF ? (
                    <div className="w-full h-full flex items-center justify-center p-4">
                        <iframe
                            src={`/${currentMedia.file_path}`}
                            className="w-full h-full max-w-5xl max-h-[80vh] rounded-lg shadow-2xl"
                            title={currentMedia.file_name}
                        />
                    </div>
                ) : (
                    <img
                        src={`/${currentMedia.file_path}`}
                        alt={currentMedia.file_name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.src = 'images/products/Wooden Box.png'; }}
                    />
                )}

                {/* Navigation Arrows */}
                {hasMultiple && (
                    <>
                        <button
                            onClick={goToPrevious}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors"
                        >
                            <i className="fas fa-chevron-left text-xl"></i>
                        </button>
                        <button
                            onClick={goToNext}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors"
                        >
                            <i className="fas fa-chevron-right text-xl"></i>
                        </button>
                    </>
                )}
            </div>

            {/* Thumbnail Strip */}
            {hasMultiple && media.length > 2 && (
                <div className="bg-gray-900 p-3 flex justify-center gap-2 overflow-x-auto">
                    {media.map((m, idx) => (
                        <button
                            key={m.id}
                            onClick={() => setCurrentIndex(idx)}
                            className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                idx === currentIndex ? 'border-blue-500 opacity-100' : 'border-transparent opacity-60 hover:opacity-80'
                            }`}
                        >
                            {m.file_type === 'pdf' ? (
                                <div className="w-full h-full flex items-center justify-center bg-red-900/30">
                                    <i className="fas fa-file-pdf text-red-400 text-lg"></i>
                                </div>
                            ) : (
                                <img src={`/${m.file_path}`} alt={m.file_name} className="w-full h-full object-cover" onError={(e) => { e.target.src = 'images/products/Wooden Box.png'; }} />
                            )}
                        </button>
                    ))}
                </div>
            )}

            {/* Close button overlay for mobile */}
            <button
                onClick={onClose}
                className="absolute top-16 right-4 bg-black/50 hover:bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors md:hidden"
            >
                <i className="fas fa-times text-lg"></i>
            </button>
        </div>
    );
}
